import { DATE_TIME_FORMAT } from "@rageeb/frontend-lib";
import type { Locale } from "date-fns";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import i18n from "@/i18n";
import type { LanguageCode } from "@/i18n/constants";
import { DEFAULT_LANG } from "@/i18n/constants";

type DateInput = Date | string | number;

const LOCALE_MAP: Record<LanguageCode, Locale> = {
	en: enUS,
	ar,
};

// Bidi isolation. Dates use the English `dd MMM yyyy` shape whose day / month /
// year are separated by spaces. Inside an RTL (Arabic) page the Unicode
// Bidirectional Algorithm reorders those runs — "06 Jul 2026" renders as
// "Jul 2026 06". Wrapping the value in a LEFT-TO-RIGHT ISOLATE forces it to lay
// out left-to-right in every language, so the date reads identically to English
// regardless of page direction. The isolate characters are invisible.
const LRI = "\u2066"; // LEFT-TO-RIGHT ISOLATE
const PDI = "\u2069"; // POP DIRECTIONAL ISOLATE

/**
 * Wrap arbitrary text in a left-to-right isolate so it renders LTR even inside
 * an RTL page. Use for composed date/number strings (e.g. a "start - end"
 * range) that the bidi algorithm would otherwise reorder.
 */
const ltr = (text: string): string => `${LRI}${text}${PDI}`;

const toDate = (input: DateInput): Date =>
	typeof input === "string" ? parseISO(input) : new Date(input);

const resolveLocale = (): Locale =>
	LOCALE_MAP[i18n.language as LanguageCode] ?? LOCALE_MAP[DEFAULT_LANG];

/**
 * Format a date for display. Product decision (FCL-46985): dates render in the
 * English format in every language — always English month / AM-PM markers and
 * always left-to-right — so switching to Arabic never changes how a date looks.
 *
 * The result is wrapped in an LTR isolate, so render it directly; do NOT use it
 * for filenames, API params or `<input>` values — call `date-fns/format`
 * (already English and clean) for those. Defaults to `DATE_TIME_FORMAT`.
 */
const formatDate = (
	value: DateInput,
	pattern: string = DATE_TIME_FORMAT,
): string => ltr(format(toDate(value), pattern, { locale: enUS }));

// date-fns v2 exposes no named options type for formatDistanceToNow.
type RelativeToNowOptions = {
	includeSeconds?: boolean;
	addSuffix?: boolean;
};

/**
 * Locale-aware wrapper around `date-fns/formatDistanceToNow`. Resolves the
 * relative-time words ("about 2 hours ago" / "منذ ساعتين تقريبًا") to the active
 * `i18n.language`. Defaults to `addSuffix: true`; pass options to override.
 */
const formatRelativeToNow = (
	value: DateInput,
	options: RelativeToNowOptions = {},
): string =>
	formatDistanceToNow(toDate(value), {
		addSuffix: true,
		...options,
		locale: resolveLocale(),
	});

export { formatDate, formatRelativeToNow, ltr, LOCALE_MAP };
export type { DateInput };
